/* eslint-disable no-nested-ternary */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import cx from 'classnames';
import BigNumber from 'bignumber.js';

import Section from 'app/components/common/Section';
import InfoTab from 'app/components/ui/InfoTab';
import TransactionsTable from 'app/components/tables/TransactionsTable';
import TokenHeadSection from 'app/components/ui/TokenHeadSection';
import {
  useGetPairsByTokenLazyQuery,
  useGetTokenPlotLiquidityLazyQuery,
  useGetTokenPlotPriceQuery,
  useGetTokenPlotVolumeLazyQuery,
  useGetTokenQuery,
  useGetTransactionsByTokenLazyQuery,
} from 'app/generated/graphql';
import Graphic from 'app/components/graphs/Graphic';
import { GraphicTypes } from 'app/components/graphs/types';
import {
  getSlice,
  getTokenLogo, shortize, transformMutezToTez,
} from 'app/utils/helpers';
import { TokenDetailsType } from 'app/types/head-section';
import { InfoTabsDataType } from 'app/types/info-tabs';
import { useCurrency } from 'app/context/CurrencyContext';
import { TransactionsScopeEnum } from 'app/types/transactions';
import TopPairsTable from 'app/components/tables/TopPairsTable';
import { useBreadcrumbs } from 'app/context/BreadcrumbsContext';
import NotFound from 'app/pages/not-found';

import s from './TokenDetails.module.sass';

enum SelectGraphicsTypesEnum {
  Liquidity = 'Liquidity',
  Volume = 'Volume',
  Price = 'Price',
}

const selectGraphicsTypes = [
  {
    id: 1,
    type: SelectGraphicsTypesEnum.Price,
  },
  {
    id: 2,
    type: SelectGraphicsTypesEnum.Liquidity,
  },
  {
    id: 3,
    type: SelectGraphicsTypesEnum.Volume,
  },
];

const TokensDetails: React.FC = () => {
  const params: { token: string } = useParams();

  const [
    currentGraphicType,
    setCurrentGraphicType,
  ] = useState<SelectGraphicsTypesEnum>(SelectGraphicsTypesEnum.Price);

  const location = useLocation();
  const { currency, xtzUsdQuote } = useCurrency();
  const { setBreadcrumbs } = useBreadcrumbs();

  const tokenIds = useMemo(() => (params.token.split('_')), [params.token]);

  const {
    data, loading, error, refetch,
  } = useGetTokenQuery(
    {
      variables: {
        id: tokenIds[0],
        tokenId: tokenIds[1],
        currency,
      },
    },
  );

  useEffect(() => {
    refetch(
      {
        id: tokenIds[0],
        tokenId: tokenIds[1],
        currency,
      },
    );
  }, [currency, refetch, tokenIds]);

  const [fetchPairsdata, { data: pairsData }] = useGetPairsByTokenLazyQuery();

  const breadcrumbs = useMemo(() => ({
    current: loading
      ? '...'
      : error ? 'Not found'
        : `${getSlice(data?.token.symbol!) ?? 'Unnamed'} (${tokenIds[0] !== 'Tez' ? shortize(tokenIds[0]) : tokenIds[0]}) ${tokenIds[1] ? `ID: ${tokenIds[1]}` : ''}`,
    prev: location.pathname,
  }), [data?.token.symbol, error, loading, location.pathname, tokenIds]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, [breadcrumbs, setBreadcrumbs]);

  const { data: plotPrice, loading: plotPriceLoading } = useGetTokenPlotPriceQuery(
    {
      variables: {
        id: tokenIds[0],
        tokenId: tokenIds[1],
      },
    },
  );

  const [
    getPlotLiquidity,
    { data: plotLiquidity, loading: plotLiquidityLoading },
  ] = useGetTokenPlotLiquidityLazyQuery(
    {
      variables: {
        id: tokenIds[0],
        tokenId: tokenIds[1],
      },
    },
  );

  const [
    getPlotVolume,
    { data: plotVolume, loading: plotVolumeLoading },
  ] = useGetTokenPlotVolumeLazyQuery(
    {
      variables: {
        id: tokenIds[0],
        tokenId: tokenIds[1],
      },
    },
  );

  const currentGraphicData = useMemo(() => {
    if (currentGraphicType === SelectGraphicsTypesEnum.Volume) {
      const oldData = plotVolume && data && data.token.volume24h
        ? [...plotVolume?.token.plotVolume]
        : undefined;
      if (oldData && data && data.token.volume24h) {
        oldData.push({
          time: Date.now() / 1e3,
          value: new BigNumber(data.token.volume24h).div(1e6).toNumber(),
          xtzUsdQuoteHistorical: xtzUsdQuote,
        });
      }
      return ({
        data: oldData,
        loading: plotVolumeLoading,
        type: GraphicTypes.Histogram,
      });
    }
    if (currentGraphicType === SelectGraphicsTypesEnum.Price) {
      return ({
        data: plotPrice?.token.plotPrice,
        loading: plotPriceLoading,
        type: GraphicTypes.Candlestick,
      });
    }
    const oldData = plotLiquidity && data && data.token.liquidity
      ? [...plotLiquidity?.token.plotLiquidity]
      : undefined;
    if (oldData && data && data.token.liquidity) {
      oldData.push({
        time: Date.now() / 1e3,
        value: new BigNumber(data.token.liquidity).div(1e6).toNumber(),
        xtzUsdQuoteHistorical: xtzUsdQuote,
      });
    }
    return ({
      data: oldData,
      loading: plotLiquidityLoading,
      type: GraphicTypes.Linear,
    });
  }, [
    currentGraphicType,
    plotLiquidity,
    plotLiquidityLoading,
    plotPrice,
    plotPriceLoading,
    plotVolume,
    plotVolumeLoading,
    data,
    xtzUsdQuote,
  ]);

  const handleGraphicChange = useCallback((el: SelectGraphicsTypesEnum) => {
    if (el === SelectGraphicsTypesEnum.Liquidity) {
      getPlotLiquidity();
    }
    if (el === SelectGraphicsTypesEnum.Volume) {
      getPlotVolume();
    }
    setCurrentGraphicType(el);
  }, [getPlotLiquidity, getPlotVolume]);

  const [fetchCurrentType, { data: transactionData }] = useGetTransactionsByTokenLazyQuery();

  const tokenHeadSection: TokenDetailsType = useMemo(() => ({
    id: data ? data.token.id! : '',
    symbol: data ? getSlice(data.token.symbol!) : 'Symbol',
    name: data ? data.token.name! : 'Name',
    standard: data ? data.token.standard : 'FA2',
    icon: data ? getTokenLogo(data.token.icon!) : null,
    price: data ? data.token.symbol === 'XTZ' ? xtzUsdQuote : +data.token.price! : 0,
    address: data ? data.token.id! : 'Not exist',
    tokenId: data && data.token.standard === 'FA2' ? data.token.tokenId! : '',
    change: {
      day: data ? data.token.priceChange.day : 'inf',
      week: data ? data.token.priceChange.week : 'inf',
      month: data ? data.token.priceChange.month : 'inf',
    },
  }), [data, xtzUsdQuote]);

  const infoTabsData = useMemo(() => [
    {
      id: 1,
      title: 'Total liquidity:',
      value: data ? transformMutezToTez({
        value: +data.token.liquidity / 2,
        fraction: 2,
        currency,
        xtzUsdQuote,
      }) : 0,
      change: {
        day: data ? data.token.liquidityChange.day : 0,
        week: data ? data.token.liquidityChange.week : 0,
        month: data ? data.token.liquidityChange.month : 0,
      },
    },
    {
      id: 2,
      title: 'Volume (24h):',
      value: data ? transformMutezToTez({
        value: +data.token.volume24h,
        fraction: 2,
        currency,
        xtzUsdQuote,
      }) : 0,
      change: {
        day: data ? data.token.volumeChange.day : 0,
        week: data ? data.token.volumeChange.week : 0,
        month: data ? data.token.volumeChange.month : 0,
      },
    },
    {
      id: 3,
      title: 'Transactions (24h):',
      value: data ? data.token.transactionsCount24h : 0,
    },
  ] as InfoTabsDataType[], [currency, data, xtzUsdQuote]);

  if (error) {
    return (
      <NotFound />
    );
  }

  const seo = {
    priceInUsd: (Number(data?.token.price) * xtzUsdQuote).toFixed(2),
    valume24h: data ? transformMutezToTez({
      value: +data.token.volume24h!,
      fraction: 2,
      currency,
      xtzUsdQuote,
    }) : 0,
  };

  return (
    <>
      <Helmet>
        <title>{`QuipuSwap Statistic - ${loading ? '...' : data?.token.name ?? (data?.token.symbol ?? 'Unnamed')} (${loading ? '...' : data?.token.symbol ?? 'Unnamed'}) info`}</title>
        <meta
          name="description"
          content={`
Learn about: \
${data?.token.name ?? (data?.token.symbol ?? 'Unnamed')} Token on Quipuswap. \
Current price is ${seo.priceInUsd} USD. \
Volume ${seo.valume24h} USD 24H. Click to see more information. \
          `.trim()}
        />
      </Helmet>

      <TokenHeadSection
        token={tokenHeadSection}
        xtzUsdQuote={xtzUsdQuote}
        loading={loading}
        id={`${data?.token.id}[${data?.token.tokenId}]`}
        currency={currency}
      />

      <Section title="Token stats">
        <div className={cx(s.wrapper)}>
          <div className={cx(s.tabs)}>
            {infoTabsData.map(({
              id, title, value, change,
            }) => (
              <InfoTab
                key={id}
                title={title}
                value={value}
                change={change}
                className={cx(s.tab)}
                currency={currency}
                loading={loading}
              />
            ))}
          </div>

          <div className={cx(s.graph)}>
            <Graphic
              selectItems={selectGraphicsTypes}
              selectActive={currentGraphicType}
              setSelectActive={(el) => handleGraphicChange(el)}
              theme={currentGraphicData.type}
              data={currentGraphicData.data}
              loading={currentGraphicData.loading}
              tooltipTitle={currentGraphicType}
              preloaderClassName={s.preloader}
            />
          </div>
        </div>
      </Section>

      <Section title={params.token === 'Tez' ? 'Top pairs' : 'Paired with XTZ'}>
        <TopPairsTable
          data={pairsData}
          pageSize={10}
          fetch={fetchPairsdata}
          tokenIds={tokenIds}
        />
      </Section>

      <Section title="Transactions">
        <TransactionsTable
          data={transactionData}
          scope={TransactionsScopeEnum.TOKEN}
          fetchCurrentType={fetchCurrentType}
          tokenIds={tokenIds}
          transactionsByToken
        />
      </Section>
    </>
  );
};

export default TokensDetails;
